import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  axApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { getRemoteUtilityId } from "@shared/utils/getUtilityId";

export const axNetworksApi = {
  getBackupStatus: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", "choices/backup_status");
    const response = await axApiClient.get(url);
    return response.data;
  },

  getNetworkType: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", "choices/network_type");
    const response = await axApiClient.get(url);
    return response.data;
  },

  getOperationalStatus: async (): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", "choices/operational_status");
    const response = await axApiClient.get(url);
    return response.data.result;
  },

  getOriginFacilities: async (params: {
    remote_utility_id: number;
    is_all_data: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "facilities",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_all_data", params.is_all_data);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getUserInfo: async (params: {
    remote_utility_id: number;
    page: any;
    limit: any;
    role: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-info",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("page", params.page);
        qs.push("limit", params.limit);
        qs.push("role", params.role);
      }
    );
    const response = await authApiClient.get(url);
    return response.data;
  },
  getCriticalityData: async (params: {
    remote_utility_id: string;
    show_inactive: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/criticality",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("show_inactive", params.show_inactive);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getNetworksList: async (filters?: Record<string, any>): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (qs) => {
        if (filters) {
          // Required parameters
          if (filters.remote_utility_id) {
            qs.push("remote_utility_id", filters.remote_utility_id);
          }

          if (filters.utility_service) {
            qs.push("utility_service", filters.utility_service);
          }

          if (filters.page) {
            qs.push("page", filters.page);
          }

          if (filters.limit) {
            qs.push("limit", filters.limit);
          }

          if (filters.is_pagination_required !== undefined) {
            qs.push("is_pagination_required", filters.is_pagination_required);
          }

          // Optional filter parameters
          if (filters.search) {
            qs.push("search", filters.search);
          }

          if (filters.type) {
            qs.push("type", filters.type);
          }

          if (filters.status) {
            qs.push("status", filters.status);
          }

          if (filters.sort) {
            qs.push("sort", filters.sort);
          }
        }
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getNetworksSummary: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks/dashboard",
      (qs) => {
        qs.push("remote_utility_id", getRemoteUtilityId());
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  addNetwork: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", "networks");
    const response = await axApiClient.post(url, payload);
    return response.data;
  },
  updateNetwork: async (networkId: string, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", `networks/${networkId}`);
    const response = await axApiClient.patch(url, payload);
    return response.data;
  },
  updateCriticalityAssessment: async (
    networkId: string,
    payload: any
  ): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", `networks/${networkId}`);
    const response = await axApiClient.patch(url, payload);
    return response.data;
  },

  addDocuments: async (networkId: string, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", `networks/${networkId}/documents`);
    const response = await axApiClient.post(url, payload);
    return response.data;
  },
  getUnits: async (params: {
    remote_utility_id: number;
    utility_service: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "utility/services",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("utility_service", params.utility_service);
      }
    );
    const response = await authApiClient.get(url);
    return response.data;
  },

  getTerritoryZones: async (params: {
    remote_utility_id: number;
    show_inactive: boolean;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", "zone");
        qs.push("show_inactive", params.show_inactive);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getTerritoryAreas: async (params: {
    remote_utility_id: number;
    show_inactive: boolean;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", "area");
        qs.push("show_inactive", params.show_inactive);
      }
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getTransmissionNetworks: async (params: {
    remote_utility_id: number;
    is_pagination_required: number;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (qs) => {
        qs.push("type", "transmission");
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_pagination_required", params.is_pagination_required);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getDMADistributionNetworks: async (params: {
    remote_utility_id: number;
    is_pagination_required: number;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (qs) => {
        qs.push("type", "dma");
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_pagination_required", params.is_pagination_required);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getDistributionNetworks: async (params: {
    remote_utility_id: number;
    is_pagination_required: number;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (qs) => {
        qs.push("type", "distribution");
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_pagination_required", params.is_pagination_required);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getDestinationNetwork: async (params: {
    type: string;
    remote_utility_id: number;
    is_pagination_required: number;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "networks",
      (qs) => {
        qs.push("type", params.type);
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("is_pagination_required", params.is_pagination_required);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },

  getNetworkDetails: async (params: {
    networkId: string;
    tab: string;
    remote_utility_id?: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `networks/${params.networkId}/${params.tab}`,
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        if (params.tab === "performance") qs.push("date_filter", "monthly");
      }
      // `networks/${params.networkId}/${params.tab}`
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getComplianceOverview: async (params: {
    remote_utility_id: any;
    entity_type: any;
    entity_id: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "compliance/parameter-monitoring",
      (qs) => {
        qs.push("entity_type", params.entity_type);
        qs.push("entity_id", params.entity_id);
        qs.push("remote_utility_id", params.remote_utility_id);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getComplianceHistory: async (params: {
    entity_type: any;
    entity_id: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "compliance/history",
      (qs) => {
        qs.push("entity_type", params.entity_type);
        qs.push("entity_id", params.entity_id);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getAssetList: async (params: {
    remote_utility_id: any;
    page: number;
    limit: number;
    network: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "assets",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("page", params.page);
        qs.push("limit", params.limit);
        qs.push("network", params.network);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getDetails: async (params: { networkId: string }): Promise<any> => {
    const url = ApiEndpoints.createUrl("ax", `networks/${params.networkId}`);
    const response = await axApiClient.get(url);
    return response.data;
  },

  //https://api-ax-dev.bynry.com/api/ax/networks/4/documents/
  getDocuments: async (params: { networkId: string }): Promise<any> => {
    const url = ApiEndpoints.createUrl(
      "ax",
      `networks/${params.networkId}/documents`
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getServiceOrderData: async (params: {
    remote_utility_id: number;
    request_id: any;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "service-order",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("request_id", params.request_id);
      }
    );
    const response = await axApiClient.get(url);
    return response.data;
  },
  getAssets: async (remoteUtilityId: string, filters: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      "assets",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("is_pagination_required", 1);

        if (filters.networkId) {
          query.push("network", filters.networkId);
        }

        query.push("page", filters.page || 1);
        query.push("limit", filters.limit || 25);

        if (filters.searchData) {
          query.push("search", filters.searchData);
        }

        if (filters.systemNetwork) {
          query.push("system_network", filters.systemNetwork);
        }

        if (filters.location) {
          query.push("location", filters.location);
        }

        if (filters.conditionScore) {
          query.push("condition_score", filters.conditionScore);
        }

        if (filters.riskScore) {
          query.push("risk_score", filters.riskScore);
        }

        if (filters.uptime) {
          query.push("uptime", filters.uptime);
        }
      }
    );

    const response = await axApiClient.get(url);
    return response.data;
  },

  deleteNetwork: async (
    networkId: string,
    remoteUtilityId: string
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "ax",
      `networks/${networkId}`,
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
      }
    );

    const response = await axApiClient.delete(url);
    return response.data;
  },
};
